//#region  CSS/SCSS
import 'primeflex/primeflex.css';
import 'primeicons/primeicons.css';
import 'primereact/resources/primereact.css';
import 'primereact/resources/themes/saga-blue/theme.css';
import 'remixicon/fonts/remixicon.css';
import './App.css';
import './fabrix/themes/fabrix.scss';

//#endregion

import ErrorBoundary from "fabrix/components/error-handling/error-boundary";
import { ClientVerifiedCredentialsService } from "fabrix/services/modules/core/client-verified-credentials.service";
import { ModuleInfo } from "fabrix/types";
import { useCallback, useEffect } from "react";
import { useDispatch } from "react-redux";
import { createBrowserRouter, RouteObject, RouterProvider } from "react-router-dom";
import { JwtAuthService, useModules } from './fabrix';
import { setShowTranslationKeys } from './fabrix/common/global';
import { fabrixIcons } from './fabrix/common/icons';
import { FbxLog } from './fabrix/common/utils/logging.utils';
import { updateTheme } from './fabrix/common/utils/style-utils';
import { RequireAuth } from "./fabrix/components/authentication/auth-validation";
import { ErrorElement } from "./fabrix/components/error-handling/error-element";
import { useCurrentUser } from './fabrix/hooks/use-current-user';
import { setIcons } from "./fabrix/redux/icons-slice";
import { getUserModules, setLoadedModule, setModules } from "./fabrix/redux/module-slice";
import { getNotificationCounts } from "./fabrix/redux/notification-slice";
import { getVersion } from "./fabrix/redux/settings-slice";
import Home from "./fabrix/routes/home";
import Layout from "./fabrix/routes/layout";
import SigninPage from "./fabrix/routes/nt-signin";
import ResetPage from "./fabrix/routes/nt-signin/reset"; // Assuming you have a ResetPage component for handling password resets

import { LoadingService } from './fabrix/services/authentication/loading.service';
import { UserCache } from './fabrix/services/caches';
import { notificationInitSockets } from "./fabrix/services/socket/notification-socket.service";

import {
  EMPLOYEE_CENTRE_CHALLENGE_LS_KEY,
  EMPLOYEE_CENTRE_MODULE_NAME,
  hasGovernmentEmployeeCredential
} from "./modules/employee-centre/credential-access";
import { moduleRoutes } from './modules';

const appModuleRoutes = moduleRoutes();


export const buildRootRoutes = () => {
  let layout: RouteObject = {
    path: "/",
    id: "route-layout",
    element: (
      <RequireAuth>
        <Layout />
      </RequireAuth>
    ),
    children: new Array<any>(),
    errorElement: <ErrorElement />,
  };
  let signin = {
    index: true,
    id: "route-signin",
    path: "signin",
    element: <SigninPage />,
  };
  let reset = {
    index: true,
    id: "route-reset",
    path: "reset/:id",
    element: <ResetPage />, // Assuming you have a ResetPage component for handling password resets
  }

  let routes = [signin, reset, layout];

  layout.children?.push({
    index: true,
    id: "route-home",
    element: (
      <RequireAuth>
        <Home />
      </RequireAuth>
    ),
  });

  layout.children?.push(...appModuleRoutes);

  return routes;
};


const buildSigninRedirectUrl = () => {
  const currentPath = window.location.pathname.replace(/^\//, "");
  if (currentPath.startsWith("reset")) {
    return window.location.pathname;
  }
  const currentParams = new URLSearchParams(window.location.search);

  const params = new URLSearchParams();

  if (currentPath && currentPath !== "signin") {
    params.set("redirect", currentPath);
  }

  currentParams.forEach((value, key) => {
    if (key !== "redirect") {
      params.set(key, value);
    }
  });

  return `/signin?${params.toString()}`;
};

const redirectUrls = ["/signin", "/reset"];

if (!JwtAuthService.isAuthenticated() && !redirectUrls.includes(window.location.pathname)) {
  window.history.replaceState(null, "", buildSigninRedirectUrl());
}


const routeDef = buildRootRoutes();

const router = createBrowserRouter(routeDef);

const applyEmployeeCentreCredentialGuard = async (modules: ModuleInfo[]): Promise<ModuleInfo[]> => {
  const hasEmployeeCentre = modules.some((module) => module.name === EMPLOYEE_CENTRE_MODULE_NAME);
  if (!hasEmployeeCentre) {
    return modules;
  }

  try {
    const verifiedCredentialsService = new ClientVerifiedCredentialsService();
    const registrations = await verifiedCredentialsService.getRegistrations();
    const canAccess = hasGovernmentEmployeeCredential(registrations);

    if (!canAccess) {
      localStorage.removeItem(EMPLOYEE_CENTRE_CHALLENGE_LS_KEY);
    }

    return modules.map((module) => {
      if (module.name !== EMPLOYEE_CENTRE_MODULE_NAME) {
        return module;
      }

      return { ...module, hide: !canAccess };
    });
  } catch {
    localStorage.removeItem(EMPLOYEE_CENTRE_CHALLENGE_LS_KEY);

    return modules.map((module) => {
      if (module.name !== EMPLOYEE_CENTRE_MODULE_NAME) {
        return module;
      }

      return { ...module, hide: true };
    });
  }
};

function App() {
  // const [_loaded, setLoaded] = useState<boolean>(false);


  const dispatch = useDispatch<any>();
  const _currentUser = useCurrentUser();
  const _modules = useModules();

  useEffect(() => {
    /** The version has no authentication on it. */
    dispatch(getVersion());
    let service = new LoadingService();

    let debugOptions = UserCache.debugOptions;
    if (debugOptions) {
      setShowTranslationKeys(debugOptions.showTranslationKeys ?? false);
    }

    /** Loads the modules from the modules.json file. 
     * These are loaded from  a local json file and are not dependent on the user being logged in.
    */
    service.loadModules()

    /** Load the current user if someone is logged in,
     * else load the sign in page. 
     * The theme and meta data need authentication to be loaded first, so we load the current user first.
     * If the user is not logged in they will be signed in via the signin page and the next useEffect will trigger.
     */
    service.loadCurrentUser().then((user) => {
      if (user) {
        FbxLog.log("3: Current user loaded", user);

        //setLoaded(true);

        dispatch(setLoadedModule("core"));
        FbxLog.log("4: App loaded, current user", user);

      } else {
        const url = buildSigninRedirectUrl();
        FbxLog.log("3: No current user, redirecting to signin page");
        router.navigate(url, { replace: true });
        // setLoaded(true);
      }

    });
  }, []);

  /** Once the user and the modules are loaded we can load the 
   * features that require user authentication like the notifications.*/
  useEffect(() => {

    if (!_currentUser?.id || !_modules || _modules.length === 0) {
      return;
    }
    let service = new LoadingService();
    if (_modules.length > 0) {
      dispatch(getUserModules());
    }
    service.setMetaData().then((x) => {
    });

    notificationInitSockets();
    dispatch(getNotificationCounts(""));

  }, [_currentUser?.id, _modules]);






  /** Loads the modules from the module.json file. There will 
   * be no user authenticated at this point, so we cannot load the modules from the server yet.
   */
  const loadModules = useCallback(async () => {

   

  }, [_currentUser?.id]);

  return (
    <ErrorBoundary>
      <RouterProvider router={router} fallbackElement={<p>Loading...</p>} />
    </ErrorBoundary>
  );
}

export default App;
